import {
  HHAlert,
  HHButton,
  HHStack,
  HHTable,
  HHTableBody,
  HHTableCell,
  HHTableContainer,
  HHTableHead,
  HHTableRow,
} from '@hinge-health/react-component-library';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../constants/strings/routes';

export interface SubmissionResultRow {
  clientId: number;
  cap: number;
  startDate: string;
  endDate: string;
  success: boolean;
  error: string | null;
}

interface BatchImportSummaryProps {
  results: SubmissionResultRow[];
  clientNameMap: Record<number, string>;
  onRetryFailed: (failedRows: SubmissionResultRow[]) => void;
}

interface ResultsTableProps {
  rows: SubmissionResultRow[];
  clientNameMap: Record<number, string>;
  showError?: boolean;
}

const formatCap = (value: number): string => value.toLocaleString('en-US');

const ResultsTable = ({
  rows,
  clientNameMap,
  showError = false,
}: ResultsTableProps): JSX.Element => (
  <HHTableContainer sx={{ flex: 1, overflow: 'auto' }}>
    <HHTable size="small" stickyHeader>
      <HHTableHead>
        <HHTableRow>
          <HHTableCell>Client ID</HHTableCell>
          <HHTableCell>Client Name</HHTableCell>
          <HHTableCell>Cap</HHTableCell>
          <HHTableCell>Start Date</HHTableCell>
          <HHTableCell>End Date</HHTableCell>
          {showError && <HHTableCell>Error</HHTableCell>}
        </HHTableRow>
      </HHTableHead>
      <HHTableBody>
        {rows.map((row, index) => (
          // ACCOUNTS-AND-PLAN-MIGRATION: row.clientId is client-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
          <HHTableRow key={`${row.clientId}-${index}`}>
            <HHTableCell>{row.clientId}</HHTableCell>
            <HHTableCell>{clientNameMap[row.clientId] ?? ''}</HHTableCell>
            <HHTableCell>{formatCap(row.cap)}</HHTableCell>
            <HHTableCell>{row.startDate}</HHTableCell>
            <HHTableCell>{row.endDate}</HHTableCell>
            {showError && (
              <HHTableCell sx={{ color: 'error.main' }}>
                {row.error}
              </HHTableCell>
            )}
          </HHTableRow>
        ))}
      </HHTableBody>
    </HHTable>
  </HHTableContainer>
);

export const BatchImportSummary = ({
  results,
  clientNameMap,
  onRetryFailed,
}: BatchImportSummaryProps): JSX.Element => {
  const successfulResults = results.filter(r => r.success);
  const failedResults = results.filter(r => !r.success);
  const navigate = useNavigate();
  return (
    <HHStack
      spacing={2}
      sx={{ padding: 2, height: '100%', overflow: 'hidden' }}
    >
      {failedResults.length > 0 && (
        <HHStack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }} spacing={1}>
          <HHAlert
            hhVariant="filled"
            severity="error"
            icon={<ErrorOutlineIcon />}
          >
            {failedResults.length} program cap failure(s) occurred.
          </HHAlert>
          <ResultsTable
            rows={failedResults}
            clientNameMap={clientNameMap}
            showError
          />
        </HHStack>
      )}

      {successfulResults.length > 0 && (
        <HHStack sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }} spacing={1}>
          <HHAlert
            hhVariant="filled"
            severity="success"
            icon={<CheckCircleOutlineIcon />}
          >
            {successfulResults.length} program caps successfully created.
          </HHAlert>
          <ResultsTable
            rows={successfulResults}
            clientNameMap={clientNameMap}
          />
        </HHStack>
      )}

      <HHStack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
        <HHButton
          hhVariant="variant-bypass"
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={(): void =>
            navigate(`${routes.billing.home}${routes.billing.programCaps}`)
          }
        >
          Back to Program Caps
        </HHButton>
        {failedResults.length > 0 && (
          <HHButton
            hhVariant="variant-bypass"
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={(): void => onRetryFailed(failedResults)}
          >
            Retry Failed Rows
          </HHButton>
        )}
      </HHStack>
    </HHStack>
  );
};
